import { useMemo, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import useFitToViewport from "../hooks/useFitToViewport";
import "./EntityCalendar.css";

// Gap left below the calendar card, and the smallest height it may shrink to.
const BOTTOM_GAP = 16;
const MIN_CAL_HEIGHT = 400;

// Paints an event as a light tint of its colour with a solid border and text, so every
// entity calendar reads the same. Expects `color` to be a 6-digit hex.
function styleEvent(info) {
    const bg = info.event.backgroundColor || "#E5E7EB";
    const border =
        info.event.borderColor || info.event.backgroundColor || "#374151";
    Object.assign(info.el.style, {
        backgroundColor: `${bg}20`,
        border: `2px solid ${border}`,
        borderRadius: "8px",
        color: border,
        fontWeight: "500",
        fontSize: "0.75rem",
    });
}

/**
 * The calendar card shared by Shifts, Job Schedules and Maintenance: same toolbar,
 * views, sizing and event look. Any FullCalendar prop passed in overrides the default.
 */
const EntityCalendar = (calendarProps) => {
    const wrapRef = useRef(null);
    // Size the card to the space left between its top and the footer, so the page itself
    // does not scroll and FullCalendar's time grid scrolls internally (like Google Calendar).
    const calHeight = useFitToViewport(wrapRef, {
        bottomGap: BOTTOM_GAP,
        minHeight: MIN_CAL_HEIGHT,
    });

    // Open the time grid an hour before now instead of at midnight.
    const scrollTime = useMemo(
        () =>
            `${String(Math.max(new Date().getHours() - 1, 0)).padStart(2, "0")}:00:00`,
        [],
    );

    return (
        <div
            ref={wrapRef}
            className="entity-calendar tw-rounded-lg tw-border tw-bg-white tw-shadow-sm tw-flex tw-flex-col"
            style={{ height: calHeight ?? undefined }}
        >
            <div className="tw-p-4 tw-flex-1 tw-min-h-0">
                <FullCalendar
                    plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                    }}
                    timeZone="local"
                    allDaySlot={false}
                    slotMinTime="00:00:00"
                    slotMaxTime="24:00:00"
                    height="100%"
                    expandRows={false}
                    views={{ dayGridMonth: { expandRows: true } }}
                    scrollTime={scrollTime}
                    eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        meridiem: "short",
                    }}
                    nowIndicator
                    eventDisplay="block"
                    dayMaxEvents
                    moreLinkClick="day"
                    eventDidMount={styleEvent}
                    {...calendarProps}
                />
            </div>
        </div>
    );
};

export default EntityCalendar;
